/**
 * React Query hooks for workplace details management
 * Provides data fetching, caching, and mutation capabilities for workplace settings
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useAuth } from '@/hooks/useAuth';
import { requestCompanySettingsRefresh } from '@/hooks/useCompanySettings';
import { workplaceService } from '../../services/business/workplaceService';
import type { WorkplaceDetails, WorkplaceDetailsUpdate } from '../../services/business/types';

// Query keys for workplace details — always tenant-scoped so a company switch
// can never serve another tenant's cached workplace record.
export const workplaceQueryKeys = {
  all: ['workplace'] as const,
  details: (companyId?: string) => [...workplaceQueryKeys.all, 'details', companyId ?? 'none'] as const,
  logo: (companyId: string) => [...workplaceQueryKeys.all, 'logo', companyId] as const,
} as const;

/**
 * Hook to fetch workplace details
 */
export const useWorkplaceDetails = (options: { enabled?: boolean } = {}) => {
  const { currentCompany } = useAuth();
  const companyId = currentCompany?.id;

  return useQuery({
    queryKey: workplaceQueryKeys.details(companyId),
    queryFn: async () => {
      const result = await workplaceService.getWorkplaceDetails();
      if (!result.success) {
        throw new Error(result.error);
      }
      return result.data;
    },
    enabled: options.enabled !== false && !!companyId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    retry: false, // Don't retry - "no company" errors are expected for some users
  });
};

/**
 * Hook to update workplace details
 */
export const useUpdateWorkplaceDetails = () => {
  const queryClient = useQueryClient();
  const { currentCompany } = useAuth();

  return useMutation({
    mutationFn: async (updates: WorkplaceDetailsUpdate) => {
      // Validate data before sending
      const validation = await workplaceService.validateWorkplaceDetails(updates);
      if (!validation.success) {
        throw new Error(validation.error);
      }

      const result = await workplaceService.updateWorkplaceDetails(updates);
      if (!result.success) {
        throw new Error(result.error);
      }
      return result.data;
    },
    onSuccess: (data) => {
      // Invalidate and refetch to prevent cache race conditions
      queryClient.invalidateQueries({ queryKey: workplaceQueryKeys.all });

      // `update_workplace_details` writes the shared company_settings row
      // (country/currency/formats) — force useCompanySettings consumers to re-read.
      requestCompanySettingsRefresh(currentCompany?.id);

      toast.success('Workplace details updated successfully');
    },
    onError: (error: Error) => {
      console.error('Error updating workplace details:', error);
      toast.error(error.message || 'Failed to update workplace details');
    },
  });
};

/**
 * Hook to upload company logo
 */
export const useUploadLogo = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (file: File) => {
      const result = await workplaceService.uploadLogo(file);
      if (!result.success) {
        throw new Error(result.error);
      }
      return result.data;
    },
    onSuccess: (data) => {
      // Invalidate workplace details to refetch with new logo URL
      queryClient.invalidateQueries({ queryKey: workplaceQueryKeys.all });
      
      toast.success('Logo uploaded successfully');
    },
    onError: (error: Error) => {
      console.error('Error uploading logo:', error);
      toast.error(error.message || 'Failed to upload logo');
    },
  });
};

/**
 * Hook to delete company logo
 */
export const useDeleteLogo = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const result = await workplaceService.deleteLogo();
      if (!result.success) {
        throw new Error(result.error);
      }
    },
    onSuccess: () => {
      // Invalidate workplace details to refetch without logo URL
      queryClient.invalidateQueries({ queryKey: workplaceQueryKeys.all });
      
      toast.success('Logo deleted successfully');
    },
    onError: (error: Error) => {
      console.error('Error deleting logo:', error);
      toast.error(error.message || 'Failed to delete logo');
    },
  });
};

/**
 * Hook to get company logo URL by company ID
 */
export const useCompanyLogo = (companyId: string) => {
  return useQuery({
    queryKey: workplaceQueryKeys.logo(companyId),
    queryFn: () => workplaceService.getCompanyLogoUrl(companyId),
    enabled: !!companyId,
    staleTime: 10 * 60 * 1000, // 10 minutes
    gcTime: 30 * 60 * 1000, // 30 minutes
  });
};

/**
 * Hook to prefetch workplace details
 * Useful for preloading data before navigation
 */
export const usePrefetchWorkplaceDetails = () => {
  const queryClient = useQueryClient();
  const { currentCompany } = useAuth();
  const companyId = currentCompany?.id;

  return () => {
    if (!companyId) return;
    queryClient.prefetchQuery({
      queryKey: workplaceQueryKeys.details(companyId),
      queryFn: async () => {
        const result = await workplaceService.getWorkplaceDetails();
        if (!result.success) {
          throw new Error(result.error);
        }
        return result.data;
      },
      staleTime: 5 * 60 * 1000,
    });
  };
};

/**
 * Hook to get cached workplace details without triggering a fetch
 */
export const useCachedWorkplaceDetails = (): WorkplaceDetails | undefined => {
  const queryClient = useQueryClient();
  const { currentCompany } = useAuth();
  return queryClient.getQueryData(workplaceQueryKeys.details(currentCompany?.id));
};

/**
 * Hook to manually invalidate workplace details cache
 */
export const useInvalidateWorkplaceDetails = () => {
  const queryClient = useQueryClient();

  return () => {
    queryClient.invalidateQueries({ queryKey: workplaceQueryKeys.all });
  };
};
